import React from "react";
import { useHistory, useLocation } from "react-router-dom";

import {
  Header as GCDKHeader,
  HeaderProps,
} from "@graffer-inc/console-design-kit";

import { GrafferNotifService } from "../../../application/domain/notifi-from-graffer/notif-from-graffer-service";
import { useAuthContextV2 } from "../../../auth-state";
import Analytics from "../../../common/analytics";
import {
  StaticFeatureFlags,
  staticFeatureFlag_EnableTtzkGuideMenu,
} from "../../../feature-flags";
import { useNotifFromGrafferPage } from "../../../hooks/useNotification";
import { AuthUtil } from "../../../utils/auth";
import envConfig from "../../../utils/env-config";
import { KintoneExportR } from "../../pages/data-transfer/kintone-export/routes";
import { HelpR } from "../../pages/help/route";
import { SettingR } from "../../pages/settings/route";
import { SmartApplyR } from "../../pages/smart-apply/route";
import { SurveyR } from "../../pages/surveys/route";
import { TtzkPortalR } from "../../pages/ttzk-portal/route";

export const Header: React.FC<{
  onLogoClick: () => void;
  title: string;
}> = ({ title, onLogoClick }) => {
  const history = useHistory();
  const { pathname } = useLocation();
  const { auth: authState, syncAuth } = useAuthContextV2();

  const notifs = useNotifFromGrafferPage();

  const handleOnClose = React.useCallback(() => {
    GrafferNotifService.updateLastCheckedAt();
  }, []);

  const notifications: HeaderProps["notifications"] = React.useMemo(() => {
    if (notifs?.length < 2) {
      return undefined;
    }
    const title = notifs[0]?.title ?? "";
    return {
      path: SettingR.NotifFromGraffer.link({}),
      onClickForTracking: () => {
        Analytics.clickNotification({
          url: SettingR.NotifFromGraffer.link({}),
          slug: "notification_list",
        });
      },
      recentContents: [
        {
          title,
          url: SettingR.NotifFromGrafferDetail.link(notifs[0].slug),
          publishedAt: notifs[0].published_at,
          onClickForTracking: () => {
            Analytics.clickNotification({
              slug: notifs[0].slug,
              url: SettingR.NotifFromGrafferDetail.link(notifs[0].slug),
            });
          },
        },
        {
          title: notifs[1]?.title ?? "",
          url: SettingR.NotifFromGrafferDetail.link(notifs[1].slug),
          publishedAt: notifs[1].published_at,
          onClickForTracking: () => {
            Analytics.clickNotification({
              slug: notifs[1].slug,
              url: SettingR.NotifFromGrafferDetail.link(notifs[1].slug),
            });
          },
        },
      ],
    };
  }, [notifs]);

  const showNewNotification = React.useMemo(() => {
    const showPopup =
      !GrafferNotifService.shouldNotDisplayBatch() &&
      !pathname.includes(SettingR.NotifFromGraffer.link({}));
    if (showPopup) {
      Analytics.showNotificationPopup();
    }
    return showPopup;
  }, []);

  const ttzkGuideDashboardURL = envConfig.matchEnvValue({
    local: "http://localhost:81",
    dev: "https://dev-dashboard.graffer.jp",
    staging: "https://staging-dashboard.graffer.jp",
    staging_sandbox: "",
    production: "",
    production_sandbox: "",
  });

  return (
    <GCDKHeader
      logoName={title}
      onLogoClick={onLogoClick}
      tenantName={authState.data?.orgName ?? ""}
      tenantUserName={authState.data?.name ?? ""}
      envText={process.env.REACT_APP_ENVIRONMENT}
      onLogout={async () => {
        await AuthUtil.logout();
        await syncAuth();
        history.push("/login");
      }}
      notifications={notifications}
      helpPath={
        authState.isOnlyUsingEnterpriseProduct()
          ? "https://help.graffer-aistudio.jp/"
          : HelpR.HelpHome.link({})
      }
      showNewNotification={showNewNotification}
      settingPath={
        authState.hasPermissions("users.create")
          ? SettingR.Users.link({})
          : SettingR.MyAccount.link({})
      }
      isDisplayNotification={!authState.isOnlyUsingEnterpriseProduct()}
      product={{
        smartApply: authState.isProductAvailable("SmartApply")
          ? {
              urlPath: SmartApplyR.Applications.link({}),
            }
          : undefined,
        survey: authState.isProductAvailable("Survey")
          ? {
              urlPath: SurveyR.Surveys.link({}),
            }
          : undefined,
        boothReserve: authState.isProductAvailable("BoothReserve")
          ? {
              urlPath: `${
                process.env.REACT_APP_ENVIRONMENT === "local"
                  ? "http://localhost:4100"
                  : window.location.origin
              }/booth-reserve/`,
            }
          : undefined,
        madoguchiEditor: authState.isProductAvailable("MadoguchiEditor")
          ? {
              urlPath: `${
                process.env.REACT_APP_ENVIRONMENT === "local"
                  ? "http://localhost:3004"
                  : window.location.origin
              }/madoguchi-editor/`,
            }
          : undefined,
        ttzkPortal: authState.isProductAvailable("Portal")
          ? {
              urlPath: TtzkPortalR.Home.link({}),
            }
          : undefined,
        aiStudio: authState.isProductAvailable("AIStudio")
          ? {
              urlPath: `${
                process.env.REACT_APP_ENVIRONMENT === "local"
                  ? "http://localhost:4901"
                  : window.location.origin
              }/aistudio/t/${authState.data?.orgSlug}/admin`,
            }
          : undefined,
        call: authState.isProductAvailable("Call")
          ? {
              urlPath: `${
                process.env.REACT_APP_ENVIRONMENT === "local"
                  ? "http://localhost:4200"
                  : window.location.origin
              }/call/`,
            }
          : undefined,
        dataSight:
          authState.isProductAvailable("SmartApply") &&
          StaticFeatureFlags.ENABLE_DATA_SIGHT
            ? {
                urlPath: `${
                  process.env.REACT_APP_ENVIRONMENT === "local"
                    ? "http://localhost:43000"
                    : window.location.origin
                }/data-sight/application`,
              }
            : undefined,
        ttzkGuide:
          authState.isProductAvailable("TtzkGuide") &&
          staticFeatureFlag_EnableTtzkGuideMenu(authState)
            ? {
                urlPath: `${ttzkGuideDashboardURL}/guides`,
              }
            : undefined,
        kintoneExport: authState.isProductAvailable("KintoneExport", [
          "SelfServe",
        ])
          ? {
              urlPath: KintoneExportR.KintoneExportTop.link({}),
            }
          : undefined,
        ninteiShoukai: authState.isProductAvailable("NinteiShoukai")
          ? {
              urlPath:
                process.env.REACT_APP_ENVIRONMENT === "local"
                  ? "http://localhost:6803"
                  : `${window.location.origin}/nintei-shoukai/`,
            }
          : undefined,
      }}
      verified={authState.isAuthed}
      onCloseNotification={handleOnClose}
    />
  );
};
